<script setup>
// ContactsListLayout.vue — esqueleto da pagina /contacts.
// Estrutura:
//   <section flex-col h-full>
//     <ContactListHeaderWrapper /> (toolbar sticky, search + actions)
//     <main flex-1 overflow-y-auto>
//       [active filters preview]
//       <slot />            ← tabela/empty/spinner injetados por ContactsIndex
//     </main>
//     <footer>PaginationFooter</footer>
//   </section>
//
// Refator visual densidade-alta estilo ClickUp List view:
// - Sem max-w (full width)
// - Padding lateral compacto (px-4)
// - Sticky toolbar
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import ContactListHeaderWrapper from 'dashboard/components-next/Contacts/ContactsHeader/ContactListHeaderWrapper.vue';
import ContactsActiveFiltersPreview from 'dashboard/components-next/Contacts/ContactsHeader/components/ContactsActiveFiltersPreview.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

const props = defineProps({
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, default: '' },
  showPaginationFooter: { type: Boolean, default: true },
  currentPage: { type: Number, default: 1 },
  totalItems: { type: Number, default: 100 },
  itemsPerPage: { type: Number, default: 15 },
  activeSort: { type: String, default: '' },
  activeOrdering: { type: String, default: '' },
  activeSegment: { type: Object, default: null },
  segmentsId: { type: [String, Number], default: 0 },
  hasAppliedFilters: { type: Boolean, default: false },
  isFetchingList: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:currentPage',
  'update:sort',
  'search',
  'applyFilter',
  'clearFilters',
]);

const route = useRoute();

const contactListHeaderWrapper = ref(null);

const isNotSegmentView = computed(() => {
  return route.name !== 'contacts_dashboard_segments_index';
});

const isActiveView = computed(() => {
  return route.name === 'contacts_dashboard_active';
});

const isLabelView = computed(
  () => route.name === 'contacts_dashboard_labels_index'
);

const showActiveFiltersPreview = computed(() => {
  return (
    (props.hasAppliedFilters || !isNotSegmentView.value) &&
    !props.isFetchingList &&
    !isLabelView.value &&
    !isActiveView.value
  );
});

const updateCurrentPage = page => {
  emit('update:currentPage', page);
};

const openFilter = () => {
  contactListHeaderWrapper.value?.onToggleFilters();
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <ContactListHeaderWrapper
      ref="contactListHeaderWrapper"
      :show-search="isNotSegmentView && !isActiveView"
      :search-value="searchValue"
      :active-sort="activeSort"
      :active-ordering="activeOrdering"
      :header-title="headerTitle"
      :active-segment="activeSegment"
      :segments-id="segmentsId"
      :has-applied-filters="hasAppliedFilters"
      :is-label-view="isLabelView"
      :is-active-view="isActiveView"
      @update:sort="emit('update:sort', $event)"
      @search="emit('search', $event)"
      @apply-filter="emit('applyFilter', $event)"
      @clear-filters="emit('clearFilters')"
    />
    <ContactsActiveFiltersPreview
      v-if="showActiveFiltersPreview"
      :active-segment="activeSegment"
      class="shrink-0 px-4 pt-2"
      @clear-filters="emit('clearFilters')"
      @open-filter="openFilter"
    />
    <slot name="bulk-actions" />
    <main class="flex-1 overflow-y-auto">
      <slot name="default" />
    </main>
    <footer
      v-if="showPaginationFooter"
      class="shrink-0 border-t border-n-weak bg-n-background px-3 py-2"
    >
      <!-- Wrapper anula o max-w + outline do PaginationFooter (shared,
           ainda usado por outras paginas com 60rem). Aqui na lista
           densa queremos full-width, sem caixa, mais baixo. -->
      <div
        class="[&>div]:!max-w-none [&>div]:!bg-transparent [&>div]:!outline-none [&>div]:!rounded-none [&>div]:!h-8 [&>div]:!py-0 [&>div]:before:!hidden"
      >
        <PaginationFooter
          current-page-info="CONTACTS_LAYOUT.PAGINATION_FOOTER.SHOWING"
          :current-page="currentPage"
          :total-items="totalItems"
          :items-per-page="itemsPerPage"
          @update:current-page="updateCurrentPage"
        />
      </div>
    </footer>
  </section>
</template>
